import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Users, CalendarDays, CalendarOff, Send, Zap, ClipboardList, UserX, ShieldCheck } from "lucide-react";

const STEPS = [
  { id: "staff", icon: Users, title: "1. Add your staff", body: [
    "Go to Staff and press Add staff. Enter name, mobile (04… or +61…) and pick their role. Every staff member needs a role - anyone without one shows as 'Role needed' and won't be texted.",
    "Press Roles to add, rename or remove roles.",
    "Or press Template to download a spreadsheet, fill it in, then Import Excel (roles must match the list).",
    "Tap day pills to mark days a person can never work - they'll be skipped on those days.",
  ] },
  { id: "statuses", icon: UserX, title: "2. Staff statuses", body: [
    "Available - receives shift alerts.",
    "Temporary leave - pick the start and end dates. They still get alerts before the leave starts, are skipped from the start to the end date, then switch back to Available automatically.",
    "Disabled - never receives alerts until you switch them back on.",
    "Left business - you'll be asked to confirm, then their details are permanently deleted.",
  ] },
  { id: "schedule", icon: CalendarDays, title: "3. Set the weekly schedule", body: [
    "Go to Schedule and press Add shift on any day. Set the start time and the roles needed for that start time.",
    "Use Add another shift to put several shifts on the same day, each with its own roles. Press the pencil to change a shift.",
    "Each person gets ONE text per day listing only the start times they're working, with their role, e.g. \"06:00 HQ Packing & 14:00 HQ Packing\".",
  ] },
  { id: "exceptions", icon: CalendarOff, title: "4. Public holidays & closures", body: [
    "On the Schedule page, add a date under Exceptions. No texts are sent on that date - manual or automatic.",
  ] },
  { id: "send", icon: Send, title: "5. Send alerts manually", body: [
    "Go to Send Alerts, pick the shift date and press Review recipients.",
    "Each ticked shift lists the staff with its roles, already ticked. Untick anyone not needed, or press 'Add other staff' to put someone from another role on that shift.",
    "No shift scheduled or a last-minute call-in? Add a one-off shift with its roles - it works even on holidays/closures (you'll see a warning).",
    "Check the SMS preview, press Send, then confirm in the pop-up.",
  ] },
  { id: "auto", icon: Zap, title: "6. Automatic sending", body: [
    "On Send Alerts, turn on Automatic sending, choose the send time (Brisbane time) and whether to alert for the same day or the next day.",
    "Edit the message template and note, then press Save settings. Placeholders: {name} {day} {date} {shift} {note}.",
  ] },
  { id: "summary", icon: ClipboardList, title: "7. Check delivery", body: [
    "Delivery Summary shows one row per day with delivered and undelivered texts and the reason for any failure.",
    "Press Refresh statuses to pull the latest delivery results from the carrier.",
  ] },
  { id: "account", icon: ShieldCheck, title: "8. Your account", body: [
    "Use Account (bottom of the menu) to change your password or turn on SMS two-factor login.",
    "Forgotten password? Tap 'Forgot password?' on the sign-in screen - a 6-digit code is texted to the mobile on your account so you can set a new one. No mobile saved? Ask your admin to reset it.",
    "5 wrong passwords locks the account for 15 minutes. An admin can unlock it sooner from the Admin page.",
  ] },
];

export default function GuidePage() {
  return (
    <div className="animate-fade-up max-w-3xl" data-testid="guide-page">
      <p className="uppercase text-xs tracking-[0.3em] text-muted-foreground mb-2">Help</p>
      <h1 className="font-head text-4xl sm:text-5xl font-800 tracking-tight leading-none">Guide</h1>
      <p className="text-muted-foreground mt-3 mb-8">Everything you need to roster your crew and send shift alerts.</p>
      <Card className="px-5">
        <Accordion type="single" collapsible defaultValue="staff">
          {STEPS.map((s) => (
            <AccordionItem key={s.id} value={s.id} data-testid={`guide-item-${s.id}`}>
              <AccordionTrigger className="font-head font-700 text-left hover:no-underline">
                <span className="flex items-center gap-3"><s.icon className="h-4 w-4 text-primary shrink-0" />{s.title}</span>
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2 pl-7 list-disc text-sm text-muted-foreground leading-relaxed">
                  {s.body.map((b) => <li key={b}>{b}</li>)}
                </ul>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Card>
    </div>
  );
}
